import React, { useEffect, useState } from 'react';
import { Modal, Table, Tag, Typography, Spin, Alert, Button } from 'antd';
import { inventoryService, InventoryItem } from '../../services/inventoryService';
import { formatQuantity } from '../../utils/formatters';
import { EyeOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;

interface LowStockModalProps {
  visible: boolean;
  onClose: () => void;
  onViewDetails?: (medicineId: number) => void;
}

const LowStockModal: React.FC<LowStockModalProps> = ({
  visible,
  onClose,
  onViewDetails
}) => {
  const [loading, setLoading] = useState(false);
  const [lowStockMedicines, setLowStockMedicines] = useState<InventoryItem[]>([]);

  useEffect(() => {
    if (visible) {
      loadLowStockMedicines();
    }
  }, [visible]);

  const loadLowStockMedicines = async () => {
    setLoading(true);
    try {
      const data = await inventoryService.getLowStock();
      // Lọc dữ liệu theo logic mới: chỉ hiển thị sản phẩm có tồn kho < mức tối thiểu
      const filteredData = data.filter(item => {
        const currentStock = item.current_stock || item.total_quantity;
        return currentStock < item.min_stock_level;
      });
      setLowStockMedicines(filteredData);
    } catch (error) {
      console.error('Error loading low stock medicines:', error);
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: 'Tên thuốc',
      dataIndex: 'name',
      key: 'name',
      width: 250,
      render: (name: string, record: InventoryItem) => (
        <div>
          <div style={{ fontWeight: 'bold' }}>{name}</div>
          <Text type="secondary">{record.code}</Text>
        </div>
      ),
    },
    {
      title: 'Tồn kho hiện tại',
      dataIndex: 'current_stock',
      key: 'current_stock',
      width: 150,
      align: 'right' as const,
      render: (stock: number, record: InventoryItem) => (
        <div>
          <Text strong type="danger">
            {formatQuantity(stock || record.total_quantity, record.unit)}
          </Text>
        </div>
      ),
    },
    {
      title: 'Mức tối thiểu',
      dataIndex: 'min_stock_level',
      key: 'min_stock_level',
      width: 150,
      align: 'right' as const,
      render: (level: number, record: InventoryItem) => (
        <Text>{formatQuantity(level, record.unit)}</Text>
      ),
    },
    {
      title: 'Số lô',
      dataIndex: 'lot_count',
      key: 'lot_count',
      width: 100,
      align: 'center' as const,
      render: (count: number) => (
        <Tag color={count > 0 ? 'blue' : 'red'}>
          {count} lô
        </Tag>
      ),
    },
    {
      title: 'Trạng thái',
      key: 'status',
      width: 120,
      render: (record: InventoryItem) => {
        const stock = record.current_stock || record.total_quantity;
        if (stock === 0) {
          return <Tag color="red">Hết hàng</Tag>;
        }
        if (stock < record.min_stock_level) {
          return <Tag color="orange">Tồn kho thấp</Tag>;
        }
        return <Tag color="green">Ổn định</Tag>;
      },
    },
    {
      title: 'Thao tác',
      key: 'actions',
      width: 100,
      render: (_: any, record: InventoryItem) => (
        <Button
          type="link"
          icon={<EyeOutlined />}
          onClick={() => onViewDetails?.(record.id)}
        >
          Chi tiết
        </Button>
      ),
    },
  ];

  const getSummaryStats = () => {
    const outOfStock = lowStockMedicines.filter(item => (item.current_stock || item.total_quantity) === 0).length;
    const lowStock = lowStockMedicines.filter(item => {
      const stock = item.current_stock || item.total_quantity;
      return stock > 0 && stock < item.min_stock_level;
    }).length;
    const totalQuantity = lowStockMedicines.reduce((sum, item) => sum + (item.current_stock || item.total_quantity), 0);

    return (
      <div style={{ marginBottom: 16 }}>
        <Alert
          message={`Tổng cộng: ${lowStockMedicines.length} loại thuốc cần bổ sung`}
          description={
            <div>
              <Text>Số lượng tổng: {totalQuantity.toLocaleString('vi-VN')}</Text>
              <br />
              <Text type="danger">Hết hàng: {outOfStock} loại</Text>
              <br />
              <Text type="warning">Tồn kho thấp: {lowStock} loại</Text>
            </div>
          }
          type="warning"
          showIcon
        />
      </div>
    );
  };

  return (
    <Modal
      title={
        <div>
          <Title level={4} style={{ margin: 0 }}>
            Thuốc tồn kho thấp
          </Title>
          <Text type="secondary">
            Danh sách thuốc cần bổ sung tồn kho
          </Text>
        </div>
      }
      open={visible}
      onCancel={onClose}
      footer={null}
      width={1000}
      destroyOnClose
    >
      <Spin spinning={loading}>
        {getSummaryStats()}
        
        <Table
          dataSource={lowStockMedicines}
          columns={columns}
          rowKey="id"
          pagination={{
            pageSize: 15,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => 
              `${range[0]}-${range[1]} của ${total} loại thuốc`
          }}
          size="small"
          scroll={{ x: 900 }}
        />
      </Spin>
    </Modal>
  );
};

export default LowStockModal; 